import { isEqual } from "libs/predicates";
import { IUpdatableBehaviorSubject, updatableBehaviorSubject } from "libs/updatableBehaviorSubject";
// We're importing directly from the command service's `service.ts` module in order to
// avoid a circular dependency.
// command.service/command-service-context -> createEnvironment -> KBarState -> command.service/service
import { ACTIVE_PATH$, COMMAND_EVENTS$ } from "~/environment/command.service/service";
import { DialogState } from "../withModalDialog";
import { firstValueFrom } from "rxjs";

export interface IKBarDialogData {
  path?: string[];
  query?: string;
  mode?: "hotkey" | "search";
}

class KBarStateClass extends DialogState<IKBarDialogData | undefined, undefined> {
  path$!: IUpdatableBehaviorSubject<string[]>;

  readonly query$ = updatableBehaviorSubject<NonNullable<IKBarDialogData["query"]>>("");

  readonly mode$ = updatableBehaviorSubject<NonNullable<IKBarDialogData["mode"]>>("search");

  init() {
    // We set the path$ property here in the `init()` function to avoid a potential
    // "ACTIVE_PATH$ accessed before initialization" error.
    this.path$ = ACTIVE_PATH$;

    // automaticaly close kbar after some commands are executed
    COMMAND_EVENTS$.subscribe(({ command }) => {
      // Important that we don't toggle the kbar if it is already closed
      // as that would cause the Kbar's ModalDialog isOpen$ subscription
      // to run causing the KBar to attempt to focus the previously focused
      // el (which is inappropriate if the kbar isn't actually being closed).
      if (!this.isOpen()) return;

      if (command.closeKBarOnSelect && command.showInKBar) {
        this.close();
      }
    });
  }

  open = function (data) {
    // Opening the dialog when it is already open is allowed as a
    // way to update the data passed to the dialog.
    const wasAlreadyOpen = this.isOpen();
    this._beforeOpen$.next(data);
    if (!wasAlreadyOpen) this.afterBeforeOpenCallback();

    if (data?.mode !== undefined && !isEqual(this.mode$.getValue(), data?.mode)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.mode$.update(() => data.mode!);
    }

    if (data?.path !== undefined && !isEqual(this.path$.getValue(), data?.path)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.path$.update(() => data.path!);
      // We also reset the query when the path changes. Important that this
      // code block runs before the block which updates `query$` on
      // `data?.query` changes so that that change can overwrite this change.
      this.query$.update(() => "");
    }

    if (data?.query !== undefined && !isEqual(this.query$.getValue(), data?.query)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.query$.update(() => data.query!);
    }

    this._isOpen$.next({ isOpen: true, data });
    this._afterOpen$.next(data);
    return firstValueFrom(this.afterClose$);
  } as (this: this, data?: IKBarDialogData) => Promise<undefined>;

  close = function () {
    if (!this.isOpen()) return;

    this._beforeClose$.next(undefined);
    this._isOpen$.next({ isOpen: false });
    this.mode$.update(() => "search");
    this.path$.update(() => []);
    this.query$.update(() => "");
    this.beforeAfterCloseCallback();
    this._afterClose$.next(undefined);
  } as (this: this) => void;

  reset() {
    this.query$.next("");
    this.mode$.next("search");
    this.path$.next([]);
  }
}

const KBarState = new KBarStateClass();

export { KBarState };
